/**
 * Quality / Build provenance (contract v3): every release recorded (GET /quality/provenance), newest first. Each one
 * opens (GET /quality/provenance/:id) to its statement in short (what was released, from which commits, by whom, the
 * gate, the builder) and the verify result checked now; its attestation downloads as `.intoto.jsonl`. The signing key
 * (a local development key says so) and how anyone can verify a release outside Flow with the public key
 * (GET /quality/provenance/key).
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { CaretDown, CaretRight, DownloadSimple } from '@phosphor-icons/react';
import { downloadAttestation, getProvenance, getProvenanceItem, provenanceKey } from '../../services/QualityService';
import { Pill, State, SubHead } from './QualityCommon';
import { Commits, VerifiedMark, shortKeyid } from './QualityProveCards';
import {
  PROVE_AREAS, destinationText, errorText, keySourceText, plural, provenanceGate, relativeTime, shortDigest,
} from './qualityModel';

const VERIFY_SNIPPET = `# att.intoto.jsonl: the attestation; flow-provenance.pem: the public key above
jq -j '.payloadType' att.intoto.jsonl > type
jq -r '.payload' att.intoto.jsonl | base64 -d > payload
jq -r '.signatures[0].sig' att.intoto.jsonl | base64 -d > sig.bin
printf 'DSSEv1 %s %s %s ' "$(wc -c < type | tr -d ' ')" "$(cat type)" "$(wc -c < payload | tr -d ' ')" > pae.bin
cat payload >> pae.bin
openssl pkeyutl -verify -pubin -inkey flow-provenance.pem -rawin -in pae.bin -sigfile sig.bin
jq . payload   # the in-toto statement: subjects, commits, gate`;

const Yes = ({ ok, yes, no }) => <Pill className="is-xs" tone={ok ? 'pass' : 'fail'}>{ok ? yes : no}</Pill>;
Yes.propTypes = { no: PropTypes.string.isRequired, ok: PropTypes.bool, yes: PropTypes.string.isRequired };

function VerifyResult({ verify }) {
  if (!verify) { return null; }
  return (
    <div className={`quality-prov-verify is-${verify.valid ? 'pass' : 'fail'}`} data-testid="quality-prov-verify">
      <strong>{verify.valid ? 'Verified ✓: the signature checks with a trusted key and the record matches the statement.' : 'Not verified: this release can no longer be trusted as recorded.'}</strong>
      <span className="quality-prov-facts quality-small">
        <Yes no="Signature does not check" ok={verify.signature} yes="Signature checks" />
        <Yes no="Unknown key" ok={verify.keyKnown} yes="Trusted key" />
        <Yes no="Record differs from the statement" ok={verify.statementMatches} yes="Record matches the statement" />
        {verify.current && (
          <Pill className="is-xs" tone={verify.current.matches ? 'pass' : 'info'}>{verify.current.matches ? 'The files today are this release' : 'The files changed since this release'}</Pill>
        )}
      </span>
      {(verify.errors || []).length > 0 && <ul className="quality-lines">{verify.errors.map((e) => <li key={e}>{e}</li>)}</ul>}
    </div>
  );
}

VerifyResult.propTypes = { verify: PropTypes.object };

function StatementSummary({ item }) {
  const { provenance: p, statement } = item;
  const subjects = statement?.subject || [];
  const builder = statement?.predicate?.runDetails?.builder;
  const deps = statement?.predicate?.buildDefinition?.resolvedDependencies || [];
  return (
    <dl className="quality-prov-dl quality-small" data-testid="quality-prov-statement">
      <dt>Released</dt>
      <dd>
        {subjects.length ? subjects.map((s) => <div key={s.name}>{s.name} <code className="quality-mono" title={s.digest?.sha256}>sha256:{shortDigest(s.digest?.sha256)}</code></div>) : `${plural(p.fileCount || 0, 'file')}`}
      </dd>
      <dt>Commits</dt>
      <dd>{deps.length ? deps.map((d) => <div key={d.uri || d.name}>{d.name} <code className="quality-mono" title={d.digest?.gitCommit}>{String(d.digest?.gitCommit || '').slice(0, 7)}</code></div>) : <Commits commits={p.commits} />}</dd>
      <dt>Files</dt>
      <dd>{plural(p.fileCount || 0, 'file')} · tree <code className="quality-mono" title={p.treeDigest}>{shortDigest(p.treeDigest)}</code>{p.sbomDigest ? <> · SBOM <code className="quality-mono" title={p.sbomDigest}>{shortDigest(p.sbomDigest)}</code></> : null}</dd>
      <dt>Destination</dt>
      <dd>{destinationText(p.destination)}{p.destination?.url && <> · <a href={p.destination.url} rel="noreferrer" target="_blank">{p.destination.url}</a></>}</dd>
      <dt>Published by</dt>
      <dd>{p.publishedBy?.name || p.publishedBy?.email || 'Unknown'}</dd>
      <dt>Release gate</dt>
      <dd>
        {provenanceGate(p).label}{p.runId ? ` · run ${p.runId}` : ''}
        {p.override?.reason && <div className="quality-muted">Reason: {p.override.reason}</div>}
      </dd>
      <dt>Images</dt>
      <dd>
        {p.images?.status === 'reported' && (p.images.list || []).map((img) => <div key={img.name}>{img.name} <code className="quality-mono" title={img.digest}>{shortDigest(img.digest)}</code></div>)}
        {p.images?.status === 'pending' && 'Waiting for Console to report the image digests'}
        {(!p.images || p.images.status === 'n/a') && 'None (not a container delivery)'}
      </dd>
      {builder && (<><dt>Built by</dt><dd>{builder.id}{builder.version ? ` ${Object.values(builder.version).join(', ')}` : ''}</dd></>)}
      <dt>Signed with</dt>
      <dd><code className="quality-mono">{p.keyid}</code></dd>
    </dl>
  );
}

StatementSummary.propTypes = { item: PropTypes.object.isRequired };

function Release({ projectId, release, open, onToggle, onDownload }) {
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!open || item) { return undefined; }
    let live = true;
    getProvenanceItem(projectId, release.id).then((answer) => live && setItem(answer)).catch((e) => live && setError(e));
    return () => { live = false; };
  }, [open, item, projectId, release.id]);
  const gate = provenanceGate(release);
  return (
    <li className={`quality-prov-entry${open ? ' is-open' : ''}`} data-testid={`quality-prov-entry-${release.id}`}>
      <div className="quality-prov-line">
        <button aria-controls={`quality-prov-body-${release.id}`} aria-expanded={open} className="quality-btn is-sm is-quiet" onClick={onToggle} type="button">
          {open ? <CaretDown aria-hidden size={13} /> : <CaretRight aria-hidden size={13} />}
          <span>{relativeTime(release.at)}</span>
          <span className="quality-sr"> release {release.id}</span>
        </button>
        <span className="quality-small">{destinationText(release.destination)}</span>
        <Commits commits={release.commits} />
        <Pill className="is-xs" tone={gate.tone}>{gate.label}</Pill>
        <span className="quality-grow" />
        <VerifiedMark className="is-xs" verified={release.verified} />
        <button aria-label={`Download attestation ${release.id}`} className="quality-btn is-sm" onClick={() => onDownload(release.id)} type="button"><DownloadSimple aria-hidden size={13} />Attestation</button>
      </div>
      {open && (
        <div className="quality-prov-body" id={`quality-prov-body-${release.id}`}>
          {error && <p className="quality-ink-fail quality-small" role="alert">Could not read this release: {errorText(error)}</p>}
          {!error && !item && <p className="quality-muted quality-small" role="status">Verifying…</p>}
          {item && (
            <>
              <VerifyResult verify={item.verify} />
              <StatementSummary item={item} />
            </>
          )}
        </div>
      )}
    </li>
  );
}

Release.propTypes = {
  onDownload: PropTypes.func.isRequired, onToggle: PropTypes.func.isRequired, open: PropTypes.bool, projectId: PropTypes.string.isRequired, release: PropTypes.object.isRequired,
};

export default function QualityProvenance({ projectId, onBack, focusId = null }) {
  const [list, setList] = useState(null);
  const [key, setKey] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [open, setOpen] = useState(() => new Set(focusId ? [focusId] : []));

  const load = useCallback(async () => {
    setError(null);
    try {
      const answer = await getProvenance(projectId);
      setList(answer);
      setKey((k) => k || answer.key || null);
    } catch (e) {
      setError(e);
    }
    provenanceKey(projectId).then(setKey).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (focusId) { setOpen((s) => new Set([...s, focusId])); } }, [focusId]);

  const toggle = (id) => setOpen((s) => {
    const next = new Set(s);
    if (next.has(id)) { next.delete(id); } else { next.add(id); }
    return next;
  });
  const download = async (id) => {
    setNotice(null);
    try { await downloadAttestation(projectId, id); } catch (e) { setNotice(`Could not download the attestation: ${errorText(e)}`); }
  };
  const releases = list?.provenance || [];

  return (
    <div className="quality-sub" data-testid="quality-provenance">
      <SubHead onBack={onBack} title={PROVE_AREAS.provenance.title} />
      <p className="quality-muted">{PROVE_AREAS.provenance.purpose}</p>
      {notice && <p className="quality-banner is-warn" role="alert">{notice}</p>}
      {error && !list && <State action={<button className="quality-btn" onClick={load} type="button">Try again</button>} title="Could not load the releases" tone="bad">{errorText(error)}</State>}
      {!error && !list && <div className="quality-state" role="status">Loading the releases…</div>}

      {key && (
        <section aria-labelledby="quality-prov-key-title" className="quality-card quality-pad" data-testid="quality-prov-key">
          <h2 id="quality-prov-key-title">Signing key</h2>
          <p className="quality-small">
            <code className="quality-mono">{key.keyid}</code>{key.algorithm ? ` · ${key.algorithm}` : ''}{key.version ? ` · version ${key.version}` : ''}
          </p>
          <p className={key.source === 'dev_file' ? 'quality-todo is-warn quality-small' : 'quality-muted quality-small'}>{keySourceText(key.source)}.</p>
          {list?.gateRule && <p className="quality-muted quality-small">The release gate needs signed provenance: Publish waits while signing does not work or the last attestation no longer verifies.</p>}
        </section>
      )}

      {list && (
        <section aria-labelledby="quality-prov-history-title" className="quality-card quality-pad">
          <h2 id="quality-prov-history-title">Releases{releases.length ? ` (${releases.length})` : ''}</h2>
          {releases.length ? (
            <ul aria-label="Releases" className="quality-prov-history">
              {releases.map((release) => (
                <Release key={release.id} onDownload={download} onToggle={() => toggle(release.id)} open={open.has(release.id)} projectId={projectId} release={release} />
              ))}
            </ul>
          ) : <p className="quality-muted" data-testid="quality-prov-empty">No release recorded yet: the next Publish records one.</p>}
        </section>
      )}

      {key && (
        <section aria-labelledby="quality-prov-howto-title" className="quality-card quality-pad" data-testid="quality-prov-howto">
          <h2 id="quality-prov-howto-title">Verify a release outside Flow</h2>
          <p className="quality-small">
            An attestation is a DSSE envelope holding an in-toto Statement (SLSA Provenance v1), signed with ed25519. Anyone with this public key can
            check it: no Flow account needed. Its first subject&apos;s digest is the release&apos;s file tree: sha256 over the sorted lines
            {' '}<code className="quality-mono">path\0sha256-of-the-file\n</code> of the published files.
          </p>
          {key.publicKeyPem ? <pre aria-label="Public key" className="quality-pre quality-mono">{key.publicKeyPem}</pre> : <p className="quality-muted quality-small">The public key could not be read.</p>}
          <pre aria-label="How to verify" className="quality-pre quality-mono">{VERIFY_SNIPPET}</pre>
          <p className="quality-muted quality-small">A key rotation keeps the older keys trusted, so older releases keep verifying; shortened ids in the cards read {shortKeyid(key.keyid)}.</p>
        </section>
      )}
    </div>
  );
}

QualityProvenance.propTypes = {
  // The release to open on (a card's row, a compliance evidence link).
  focusId: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
};
